// Steps/ScheduleAndMode.js - Step 3 Component
import React, { useContext } from 'react';
import { Box, Typography, Grid, FormControl, RadioGroup, FormControlLabel, Radio, FormGroup, Checkbox, Card, CardContent, Chip, Avatar, Alert} from '@mui/material';
import { NotificationsActive, Sms, WhatsApp, Email, Schedule, SendTimeExtension } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { NotificationDataContext } from '../AddEditNotification';
import { formatDateTime } from '../../../../utils/dateUtils';

const ScheduleAndMode = () => {
    const { notificationData, setNotificationData } = useContext(NotificationDataContext);

    const handleScheduleTypeChange = (event) => {
        const scheduleType = event.target.value;
        setNotificationData(prev => ({
            ...prev,
            scheduleType,
            scheduledDateTime: scheduleType === 'now' ? null : prev.scheduledDateTime
        }));
    };

    const handleDateTimeChange = (newValue) => {
        setNotificationData(prev => ({
            ...prev,
            scheduledDateTime: newValue
        }));
    };

    const handleDeliveryModeChange = (mode) => (event) => {
        if (event.target.checked) {
            setNotificationData(prev => ({
                ...prev,
                deliveryModes: [...prev.deliveryModes, mode]
            }));
        } else {
            // Don't allow unchecking if it's the only mode selected
            if (notificationData.deliveryModes.length > 1) {
                setNotificationData(prev => ({
                    ...prev,
                    deliveryModes: prev.deliveryModes.filter(m => m !== mode)
                }));
            }
        }
    };

    const deliveryOptions = [
        {
            value: 'push',
            label: 'Push Notification',
            description: 'Send instant push notifications to mobile app',
            icon: <NotificationsActive sx={{ fontSize: 30 }} />,
            available: true,
            color: '#4CAF50'
        },
        {
            value: 'sms',
            label: 'SMS',
            description: 'Send text messages to mobile numbers',
            icon: <Sms sx={{ fontSize: 30 }} />,
            available: false,
            color: '#FF9800',
            comingSoon: true
        },
        {
            value: 'whatsapp',
            label: 'WhatsApp',
            description: 'Send messages via WhatsApp Business API',
            icon: <WhatsApp sx={{ fontSize: 30 }} />,
            available: false,
            color: '#25D366',
            comingSoon: true
        },
        {
            value: 'email',
            label: 'Email',
            description: 'Send email notifications',
            icon: <Email sx={{ fontSize: 30 }} />,
            available: false,
            color: '#2196F3',
            comingSoon: true
        }
    ];

    const selectedAudienceCount = notificationData.audienceType === 'customer' 
        ? notificationData.customerFilters.selectedCustomers.length
        : notificationData.expertFilters.selectedExperts.length;

    return (
        <Box>
            <Grid container spacing={1}>
                {/* Delivery Modes Section */}
                <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: 'fit-content' }}>
                        <CardContent sx={{py:0}}>
                            <Box sx={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                                <Typography variant="subtitle1" gutterBottom sx={{ 
                                    fontWeight: 600, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1,
                                }}>
                                    <SendTimeExtension color="primary" />
                                    Delivery Channels
                                </Typography>
                                {/* Selected Modes Summary */}
                                {notificationData.deliveryModes.length > 0 && (
                                    <Box sx={{display:'flex',gap:1,alignItems:'center', bgcolor: 'success.50',  }}>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'success.800', mb: 0.5 }}>
                                            Selected Delivery Channels:
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {notificationData.deliveryModes.map((mode) => {
                                                const option = deliveryOptions.find(opt => opt.value === mode);
                                                return (
                                                    <Chip
                                                        key={mode}
                                                        label={option?.label}
                                                        size="small"
                                                        sx={{
                                                            fontSize: '0.65rem',
                                                            height: 20,
                                                            bgcolor: 'green',
                                                            color: '#fff'
                                                        }}
                                                    />
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                )}
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                Choose how to deliver your notification. At least one channel must be selected.
                            </Typography>

                            <FormControl component="fieldset" fullWidth>
                                <FormGroup>
                                    {deliveryOptions.map((option) => (
                                        <Card
                                            key={option.value}
                                            variant="outlined"
                                            sx={{
                                                mb: 1,
                                                border: '1px solid',
                                                borderColor: notificationData.deliveryModes.includes(option.value) 
                                                    ? 'primary.main' 
                                                    : option.available ? 'grey.300' : 'grey.200',
                                                bgcolor: notificationData.deliveryModes.includes(option.value) 
                                                    ? 'primary.50' 
                                                    : option.available ? 'background.paper' : 'grey.50',
                                                opacity: option.available ? 1 : 0.6,
                                                cursor: option.available ? 'pointer' : 'not-allowed'
                                            }}
                                        >
                                            <CardContent sx={{ p: 1, '&:last-child': { pb: 0 } }}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={notificationData.deliveryModes.includes(option.value)}
                                                            onChange={handleDeliveryModeChange(option.value)}
                                                            disabled={!option.available || (
                                                                notificationData.deliveryModes.length === 1 && 
                                                                notificationData.deliveryModes.includes(option.value)
                                                            )}
                                                            size="small"
                                                        />
                                                    }
                                                    label={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                                            <Avatar
                                                                sx={{
                                                                    width: 40,
                                                                    height: 40,
                                                                    bgcolor: option.available ? option.color : 'grey.400'
                                                                }}
                                                            >
                                                                {React.cloneElement(option.icon, {
                                                                    sx: { fontSize: 20, color: 'white' }
                                                                })}
                                                            </Avatar>
                                                            <Box sx={{ flex: 1 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Typography variant="subtitle2" sx={{ 
                                                                        fontWeight: 600, 
                                                                        fontSize: '0.8rem' 
                                                                    }}>
                                                                        {option.label}
                                                                    </Typography>
                                                                    {option.comingSoon && (
                                                                        <Chip 
                                                                            label="Coming Soon" 
                                                                            size="small" 
                                                                            sx={{ 
                                                                                height: 16, 
                                                                                fontSize: '0.6rem',
                                                                                bgcolor: 'warning.100',
                                                                                color: 'warning.800'
                                                                            }} 
                                                                        />
                                                                    )}
                                                                </Box>
                                                                <Typography 
                                                                    variant="body2" 
                                                                    color="text.secondary"
                                                                    sx={{ fontSize: '0.7rem', mt: 0.5 }}
                                                                >
                                                                    {option.description}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    }
                                                    sx={{ m: 0, width: '100%' }}
                                                />
                                            </CardContent>
                                        </Card>
                                    ))}
                                </FormGroup>
                            </FormControl>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Grid container spacing ={1}>
                        {/* Schedule Section */}
                        <Grid item xs={12}>
                            <Card variant="outlined" sx={{ height: 'fit-content' }}>
                                <CardContent sx={{pb:'0px !important'}}>
                                    <Typography variant="subtitle1" gutterBottom sx={{ 
                                        fontWeight: 600, 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 1,
                                        
                                    }}>
                                        <Schedule color="primary" />
                                        Scheduling Options
                                    </Typography>

                                    <FormControl component="fieldset" fullWidth>
                                        <RadioGroup
                                            value={notificationData.scheduleType}
                                            onChange={handleScheduleTypeChange}
                                            sx={{ gap: 1 }}
                                        >
                                            <Box sx={{ display: 'flex', justifyContent:'space-between' }}>
                                                <FormControlLabel
                                                    value="now"
                                                    control={<Radio />}
                                                    label={
                                                        <Box>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                                Send Now
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                                Deliver notification immediately
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    sx={{
                                                        border: '1px solid',
                                                        borderColor: notificationData.scheduleType === 'now' ? 'primary.main' : 'grey.300',
                                                        borderRadius: 1,
                                                        p: 1,
                                                        m: 0,
                                                        mb: 1,
                                                        bgcolor: notificationData.scheduleType === 'now' ? 'primary.50' : 'transparent'
                                                    }}
                                                />

                                                <FormControlLabel
                                                    value="later"
                                                    control={<Radio />}
                                                    label={
                                                        <Box>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                                Schedule for Later
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                                Set a specific date and time
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    sx={{
                                                        border: '1px solid',
                                                        borderColor: notificationData.scheduleType === 'later' ? 'primary.main' : 'grey.300',
                                                        borderRadius: 1,
                                                        p: 1,
                                                        m: 0,
                                                        bgcolor: notificationData.scheduleType === 'later' ? 'primary.50' : 'transparent'
                                                    }}
                                                />
                                            </Box>
                                        </RadioGroup>
                                    </FormControl>

                                    {notificationData.scheduleType === 'later' && (
                                        <Grid container spacing={1} sx={{ my: 1 }}>
                                            <Grid item xs={12} sm={notificationData.scheduledDateTime && 5}>
                                                <DateTimePicker
                                                    label="Schedule Date & Time"
                                                    value={notificationData.scheduledDateTime}
                                                    onChange={handleDateTimeChange}
                                                    minDateTime={dayjs().add(1, 'minute')}
                                                    closeOnSelect={false}
                                                    slotProps={{
                                                        textField: {
                                                            size: 'small',
                                                            fullWidth: true,
                                                            required: true,
                                                            InputProps: {
                                                                style: { fontSize: '0.75rem' }
                                                            }
                                                        }
                                                    }}
                                                />
                                            </Grid>                                            
                                            {notificationData.scheduledDateTime && (
                                                <Grid item xs={12} sm={7}>
                                                    <Alert severity="info" sx={{ fontSize: '0.7rem' }}>
                                                        <strong>Scheduled for:</strong> {formatDateTime(notificationData.scheduledDateTime)}
                                                    </Alert>
                                                </Grid>
                                            )}
                                        </Grid>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        

                        {/* Summary Section */}
                        <Grid item xs={12}>
                            <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                    Configuration Summary:
                                </Typography>
                                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                    <li>
                                        <strong>Recipients:</strong> {selectedAudienceCount} {notificationData.audienceType}(s)
                                    </li>
                                    <li>
                                        <strong>Schedule:</strong> {notificationData.scheduleType === 'now' ? 'Send immediately' : 
                                            notificationData.scheduledDateTime ? 
                                                `Scheduled for ${formatDateTime(notificationData.scheduledDateTime)}` :
                                                'Please select date and time'
                                        }
                                    </li>
                                    <li>
                                        <strong>Delivery Channels:</strong> {notificationData.deliveryModes.map(mode => 
                                            deliveryOptions.find(opt => opt.value === mode)?.label
                                        ).join(', ')}
                                    </li>
                                </Box>
                            </Alert>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ScheduleAndMode;